import { DNAButton, DNAContextMenu } from '@alucio/lux-ui';
import React from 'react';
import { useDispatch } from 'src/state/redux';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { drawerActions, DRAWER_ENTITIES } from 'src/state/redux/slice/drawer';
import { DocumentVersionORM, MeetingORM } from 'src/types/orms';
import DNAMeetingDeleteModal from '../DNA/Modal/DNAMeetingDeleteModal';
import { useCanDeleteMetting } from './AddMeetingFooter';
import {
  AssociatedFileType,
  ContentPresented,
  ContentPresentedStatus,
  DocumentStatus,
  Hub,
  HubSection,
  HubSectionType,
  HubStatus,
  SharedFileHubSetting,
  HubSectionItemStatus,
  MeetingType,
  MeetingStatus,
  HubType,
} from '@alucio/aws-beacon-amplify/src/models';
import { hubActions } from 'src/state/redux/slice/hub';
import format from 'date-fns/format';
import { useHistory } from 'react-router';
import { v4 as uuid } from 'uuid';
import { HUB_SECTION_STATUS, HUB_SHARED_FILE_CONTENT_TYPE } from '@alucio/aws-beacon-amplify/src/API';
import { useAllDocumentVersionMap } from 'src/state/redux/selector/document';
import { HubsIcon } from 'src/assets/icons/Hubs';
import { useIsMeetingLocked } from './AddMeetingProvider';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useDisabledWidgets } from 'src/screens/Hubs/EditHub/LeftSideBar';
import { meetingActions } from 'src/state/redux/slice/meeting';

interface Props {
  meetingORM: MeetingORM,
  isTablet: boolean,
  isCRMMeetingEnabled?: boolean,
}

const MeetingContextMenu: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const { meetingORM, isTablet, isCRMMeetingEnabled } = props;
  const { type, status } = meetingORM.model;
  const { deleteDisabled } = useCanDeleteMetting(meetingORM);
  const history = useHistory()
  const documentVersions = useAllDocumentVersionMap()
  const isMeetingLocked = useIsMeetingLocked(meetingORM)
  const enableHubs = useFeatureFlags('enableHubs')
  const hideCreateHub = useFeatureFlags('BEAC_7952_hide_create_hub')
  const enableMeetingFeatures = useFeatureFlags('enableMeetingFeatures')
  const [isWidgetDisabled] = useDisabledWidgets()
  const displayRestartMeetingOption = enableMeetingFeatures && !isMeetingLocked && isCRMMeetingEnabled &&
    (type !== MeetingType.MANUAL || (type === MeetingType.MANUAL && status === MeetingStatus.PLANNED));

  const createHubsCB = (hub: Hub) => {
    history.push(`/hubs/${hub.id}`)
  }

  const getPresentedDocumentVersionORMs =
  (contentPresented: ContentPresented[]): DocumentVersionORM[] => {
    // contentPresented.contentType returns DOCUMENT_VERSION and the useAllDocumentVersionMap returns documentVersion.
    // the const defined below maps the two together
    const items = {
      DOCUMENT_VERSION: documentVersions,
      DOCUMENT_VERSION_GROUP: documentVersions,
    }
    return contentPresented
      .filter(cp => cp.status !== ContentPresentedStatus.DELETED)
      .filter(cp => cp.contentType !== 'CUSTOM_DECK')
      .map(cp => items[cp.contentType][cp.contentId])
      .filter( content => !!content )
  }

  const getPresentedFiles = (): SharedFileHubSetting[] => {
    const presentedDocVers = getPresentedDocumentVersionORMs(meetingORM.model.contentPresented)
    const duplicateRemoved = [...new Map(presentedDocVers.map((docVer) => [docVer.model.id, docVer])).values()]
    if (presentedDocVers) {
      const result =  duplicateRemoved.map(docVerORM => {
        const associatedFiles = docVerORM.relations.associatedFiles
          .filter(associatedFile => {
            return (
              associatedFile.model.type === AssociatedFileType.ATTACHED_FILE &&
              associatedFile.meta.canBeSharedByMSL
            ) ||
            (
              associatedFile.model.type === AssociatedFileType.DOCUMENT &&
              associatedFile.meta.canBeSharedByMSL &&
              associatedFile.file?.['status'] === DocumentStatus.PUBLISHED
            )
          })
          .map(associatedFile => {
            let versionId: string | undefined
            if (associatedFile.model.type === AssociatedFileType.DOCUMENT) {
              versionId = associatedFile.relations.latestUsableDocumentVersion?.id ?? undefined
            }
            return {
              associatedFileId: associatedFile.model.id,
              versionId,
            }
          })

        return {
          id: uuid(),
          contentId: docVerORM.model.id,
          title: docVerORM.model.title!,
          contentType: HUB_SHARED_FILE_CONTENT_TYPE.DOCUMENT_VERSION,
          documentVersionSettings: docVerORM.relations.associatedFiles.length ? { associatedFiles } : undefined,
          status: HubSectionItemStatus.ACTIVE,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      })
      return result
    } else {
      return []
    }
  }

  const meetingsDefaultWidgets: Record<HubSectionType, HubSection> = {
    [HubSectionType.TEXTAREA]: {
      id: uuid(),
      sectionHeader: 'Text Box',
      status: HUB_SECTION_STATUS.ACTIVE,
      type: HubSectionType.TEXTAREA,
      visible: true,
      order: 0,
      textarea: {
        content: '',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    [HubSectionType.SHARED_FILES]: {
      id: uuid(),
      sectionHeader: 'Shared Files',
      status: HUB_SECTION_STATUS.ACTIVE,
      type: HubSectionType.SHARED_FILES,
      visible: true,
      order: 1,
      sharedFiles: getPresentedFiles(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    [HubSectionType.TODO_LIST]: {
      id: uuid(),
      sectionHeader: 'To-Do List',
      status: HUB_SECTION_STATUS.ACTIVE,
      type: HubSectionType.TODO_LIST,
      visible: true,
      toDos: [],
      order: 2,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    [HubSectionType.LINKS]: {
      id: uuid(),
      sectionHeader: 'Links',
      status: HUB_SECTION_STATUS.ACTIVE,
      type: HubSectionType.LINKS,
      visible: true,
      links: [],
      order: 3,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  }

  const handleCreateHubs = () => {
    const defaultHubSections: HubSection[] = Object
      .values(meetingsDefaultWidgets)
      .filter(hubSection => !isWidgetDisabled(hubSection.type))
      .map(hubSection => {
        // Update createdAt/updatedAt to present time
        return {
          ...hubSection,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      })

    dispatch(hubActions.createHub({
      name: meetingORM.model.title ? meetingORM.model.title : 'Hub Created ' + format(new Date(), 'LLL d, y'),
      status: HubStatus.ACTIVE,
      hubSections: defaultHubSections,
      meetingId: meetingORM.model.id,
      hubType: HubType.STANDALONE,
      callback: createHubsCB,
    }))
  }

  const openDeleteModal = () => {
    if (meetingORM) {
      const payload = {
        isVisible: true,
        allowBackdropCancel: true,
        component: () =>
          (<DNAMeetingDeleteModal
            meeting={meetingORM}
          />),
      };
      dispatch(DNAModalActions.setModal(payload));
    }
  }

  const toggleDrawer = () => {
    dispatch(drawerActions.toggle({
      entity: DRAWER_ENTITIES.MEETING,
      entityId: meetingORM.model.id,
    }));
  }

  const restartMeeting = () => {
    dispatch(meetingActions.updateMeeting(meetingORM.model, {
      ...meetingORM.model,
      status : MeetingStatus.IN_PROGRESS,
      type: type === MeetingType.MANUAL ? MeetingType.IN_PERSON : type,
    }));
    // [TODO BEAC-6488] - This probably shouldn't push us into this page until the ORM is in Redux
    history.push(`/meeting-presentation/${meetingORM.model.id}`)
  }

  return (
    <DNAContextMenu>
      <DNAContextMenu.Anchor>
        <DNAButton
          appearance="ghost"
          status="subtle"
          size={ isTablet ? 'xl' : 'md'}
          iconLeft="dots-three-vertical-bold"
        />
      </DNAContextMenu.Anchor>
      <DNAContextMenu.Items>
        {enableMeetingFeatures && (
          <DNAContextMenu.Item
            onPress={toggleDrawer}
            status="primary"
            icon="pencil"
            title="Edit"
          />
        )}
        {enableHubs && !hideCreateHub &&
          <DNAContextMenu.Item
            onPress={() => handleCreateHubs()}
            status="primary"
            title="Share files via Hub"
            CustomIcon={HubsIcon}
          />
        }
        { displayRestartMeetingOption &&
          <DNAContextMenu.Item
            key={type}
            onPress={restartMeeting}
            status="primary"
            icon="users"
            title={
              (status === MeetingStatus.ENDED_EXIT || status === MeetingStatus.ENDED_TIMEOUT)
                ? 'Restart meeting'
                : 'Start meeting'
            }
          />
        }
        {enableMeetingFeatures && !isMeetingLocked && !deleteDisabled && (
          <DNAContextMenu.Item
            onPress={openDeleteModal}
            status="danger"
            icon="trash"
            iconStatus="danger"
            title="Delete"
          />
        )}
      </DNAContextMenu.Items>
    </DNAContextMenu>
  )
}

export default MeetingContextMenu;
